import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ProofOfFundsService from "../services/ProofOfFundsService";
import UserService from "../services/UserService";

const statuses = [
  { value: "POF_NEEDED", label: "Documents needed" },
  { value: "POF_RECEIVED", label: "Ready for review" },
  { value: "POF_APPROVED", label: "Approved — awaiting processing" },
  { value: "CONFIRMED", label: "Payment confirmed" },
  { value: "POF_REJECTED", label: "Rejected" },
];

export default function ProofOfFunds() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("POF_NEEDED");
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const canDecide = UserService.hasRole(["compliance-mgr"]);

  const requestsQuery = useQuery(
    ["proofOfFunds", status],
    () => ProofOfFundsService.getRequests(status),
    { refetchOnWindowFocus: false }
  );

  const detailQuery = useQuery(
    ["proofOfFundsDetail", selected?.paymentRequestId],
    () => ProofOfFundsService.getRequest(selected.paymentRequestId),
    { enabled: !!selected, refetchOnWindowFocus: false }
  );

  const decisionMutation = useMutation(
    ({ paymentRequestId, decision: nextDecision, reason: nextReason }) =>
      ProofOfFundsService.decideRequest(
        paymentRequestId,
        nextDecision,
        nextReason
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["proofOfFunds", status]);
        queryClient.invalidateQueries([
          "proofOfFundsDetail",
          selected?.paymentRequestId,
        ]);
        setReason("");
      },
    }
  );

  const uploadMutation = useMutation(
    ({ paymentRequestId, selectedFiles }) =>
      Promise.all(
        selectedFiles.map(
          (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const base64Content = String(reader.result).split(",")[1];
                ProofOfFundsService.uploadDocument(paymentRequestId, {
                  fileName: file.name,
                  contentType: file.type,
                  size: file.size,
                  base64Content,
                })
                  .then(resolve)
                  .catch(reject);
              };
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(file);
            })
        )
      ),
    {
      onSuccess: () => {
        setFiles([]);
        queryClient.invalidateQueries([
          "proofOfFunds",
          status,
        ]);
        queryClient.invalidateQueries([
          "proofOfFundsDetail",
          selected?.paymentRequestId,
        ]);
      },
    }
  );

  const openDocument = async (document, original) => {
    const response = await ProofOfFundsService.downloadDocument(
      document.id,
      document.companyId,
      original
    );
    const url = URL.createObjectURL(response.data);
    window.open(url, "_blank", "noopener,noreferrer");
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const columns = [
    { field: "paymentRequestId", headerName: "Payment Request", width: 180 },
    { field: "companyId", headerName: "Employer", width: 110 },
    {
      field: "memberId",
      headerName: "Member",
      width: 190,
      renderCell: ({ row }) => (
        <Box>
          <Typography variant="body2">{row.memberId || "—"}</Typography>
          {row.customerName && (
            <Typography variant="caption" color="text.secondary">
              {row.customerName}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      width: 110,
      valueFormatter: ({ value }) =>
        value === null || value === undefined ? "—" : Number(value).toFixed(2),
    },
    { field: "documentCount", headerName: "Documents", width: 110 },
    { field: "status", headerName: "Status", width: 150 },
    {
      field: "updatedAt",
      headerName: "Received",
      width: 190,
      valueFormatter: ({ value }) =>
        value ? new Date(value).toLocaleString() : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          aria-label="View proof of funds"
          onClick={() => {
            setSelected(row);
            setActiveTab(0);
          }}
        >
          <VisibilityIcon />
        </Button>
      ),
    },
  ];

  const detail = detailQuery.data?.data;
  const canUpload =
    canDecide &&
    ["POF_NEEDED", "POF_RECEIVED"].includes(detail?.status);
  const rows = (requestsQuery.data?.data || []).filter((row) => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return true;
    }
    return [
      row.paymentRequestId,
      row.companyId,
      row.memberId,
      row.customerName,
      row.totalAmount,
      row.status,
    ].some((value) => String(value ?? "").toLowerCase().includes(query));
  });

  return (
    <Box sx={{ height: "calc(100vh - 100px)", width: "100%", p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Proof of Funds
        </Typography>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Payment, member, name..."
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="pof-status-label">Status</InputLabel>
          <Select
            labelId="pof-status-label"
            value={status}
            label="Status"
            onChange={(event) => setStatus(event.target.value)}
          >
            {statuses.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {requestsQuery.isLoading ? (
        <CircularProgress />
      ) : requestsQuery.isError ? (
        <Alert severity="error">Error fetching proof-of-funds requests.</Alert>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.paymentRequestId}
          components={{ Toolbar: GridToolbar }}
          disableSelectionOnClick
        />
      )}

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Proof of Funds Review</DialogTitle>
        <DialogContent>
          {detailQuery.isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : detailQuery.isError ? (
            <Alert severity="error">Unable to load the request.</Alert>
          ) : detail ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label="Payment information" />
                <Tab label="Documents" />
                <Tab label="Review decision" />
              </Tabs>

              <Card
                variant="outlined"
                sx={{ display: activeTab === 0 ? "block" : "none" }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payment information
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography>
                      <strong>Payment request:</strong>{" "}
                      {detail.paymentRequestId}
                    </Typography>
                    <Typography>
                      <strong>Employer:</strong> {detail.companyId}
                    </Typography>
                    <Typography>
                      <strong>Member:</strong> {detail.memberId || "—"}
                      {detail.customerName ? ` — ${detail.customerName}` : ""}
                    </Typography>
                    <Typography>
                      <strong>Amount:</strong> {detail.totalAmount}
                    </Typography>
                  </Stack>
                  <Chip label={detail.status} sx={{ mt: 1 }} />
                </CardContent>
              </Card>

              {canUpload && (
                <Card
                  variant="outlined"
                  sx={{ display: activeTab === 1 ? "block" : "none" }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Add new documents
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Add the proof-of-funds files received from the caller.
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button component="label" variant="outlined">
                        Select documents
                        <input
                          hidden
                          type="file"
                          multiple
                          accept="application/pdf,image/jpeg,image/png,image/webp"
                          onChange={(event) =>
                            setFiles(Array.from(event.target.files || []))
                          }
                        />
                      </Button>
                      <Typography variant="body2">
                        {files.length
                          ? `${files.length} document(s) selected`
                          : "No documents selected"}
                      </Typography>
                    </Stack>
                    {files.length > 0 && (
                      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                        {files.map((file) => (
                          <Typography key={`${file.name}-${file.size}`} variant="body2">
                            {file.name}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                    {uploadMutation.isError && (
                      <Alert severity="error" sx={{ mt: 1.5 }}>
                        Unable to upload the selected document(s).
                      </Alert>
                    )}
                    <Button
                      sx={{ mt: 1.5 }}
                      variant="contained"
                      disabled={!files.length || uploadMutation.isLoading}
                      onClick={() =>
                        uploadMutation.mutate({
                          paymentRequestId: detail.paymentRequestId,
                          selectedFiles: files,
                        })
                      }
                    >
                      {uploadMutation.isLoading
                        ? "Uploading..."
                        : "Upload documents"}
                    </Button>
                  </CardContent>
                </Card>
              )}

              <Card
                variant="outlined"
                sx={{ display: activeTab === 1 ? "block" : "none" }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Uploaded documents
                  </Typography>
                  {detail.documents?.length ? (
                    <Stack divider={<Divider />} spacing={0}>
                      {detail.documents.map((document) => (
                        <Box
                          key={document.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            py: 1,
                          }}
                        >
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography>
                              {document.originalFileName || document.documentType}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {document.status || "Uploaded"}
                            </Typography>
                          </Box>
                          <Button onClick={() => openDocument(document, false)}>
                            View
                          </Button>
                          {document.originalBlobPath && (
                            <Button onClick={() => openDocument(document, true)}>
                              Original
                            </Button>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">
                      No documents uploaded yet.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {canDecide && (
                <Card
                  variant="outlined"
                  sx={{ display: activeTab === 2 ? "block" : "none" }}
                >
                  <CardContent>
                    {detail.status === "POF_RECEIVED" &&
                    detail.documents?.length > 0 ? (
                      <>
                        <Typography variant="h6" gutterBottom>
                          Review decision
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5 }}
                        >
                          Add a remark before rejecting. Remarks are optional
                          when approving.
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Review remark"
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                        />
                        {decisionMutation.isError && (
                          <Alert severity="error" sx={{ mt: 1.5 }}>
                            Unable to save the review decision.
                          </Alert>
                        )}
                        <Stack
                          direction="row"
                          justifyContent="flex-end"
                          spacing={1}
                          sx={{ mt: 1.5 }}
                        >
                          <Button
                            color="error"
                            startIcon={<CloseIcon />}
                            disabled={
                              decisionMutation.isLoading || !reason.trim()
                            }
                            onClick={() =>
                              decisionMutation.mutate({
                                paymentRequestId: detail.paymentRequestId,
                                decision: "REJECTED",
                                reason,
                              })
                            }
                          >
                            Reject
                          </Button>
                          <Button
                            color="success"
                            variant="contained"
                            startIcon={<CheckIcon />}
                            disabled={decisionMutation.isLoading}
                            onClick={() =>
                              decisionMutation.mutate({
                                paymentRequestId: detail.paymentRequestId,
                                decision: "APPROVED",
                                reason,
                              })
                            }
                          >
                            Approve
                          </Button>
                        </Stack>
                      </>
                    ) : ["POF_NEEDED", "POF_RECEIVED"].includes(detail.status) ? (
                      <>
                        <Typography variant="h6" gutterBottom>
                          Documents required
                        </Typography>
                        <Typography color="text.secondary">
                          Upload at least one proof-of-funds document before
                          making a review decision.
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="h6" gutterBottom>
                          Review completed
                        </Typography>
                        <Typography>
                          <strong>Status:</strong> {detail.status}
                        </Typography>
                        <Typography>
                          <strong>Reviewed by:</strong>{" "}
                          {detail.reviewedBy || "—"}
                        </Typography>
                        <Typography>
                          <strong>Reviewed at:</strong>{" "}
                          {detail.reviewedAt
                            ? new Date(detail.reviewedAt).toLocaleString()
                            : "—"}
                        </Typography>
                        <Typography sx={{ mt: 1 }}>
                          <strong>Remark:</strong>{" "}
                          {detail.reviewReason || "No remark provided"}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
